import React from 'react'
import { Shield, Lock, Globe, Terminal, ExternalLink, Package } from 'lucide-react'

interface Associate {
  name: string
  persona: string
  description: string
  track: 'CR' | 'SR' | 'PR'
  trackLabel: string
  trackColor: string
  icon: React.ReactNode
  packageName: string
  cliCommand: string
  skills: Array<{
    id: string
    name: string
    description: string
    frameworks: string[]
  }>
  installCmd: string
  exampleCmd: string
}

const ASSOCIATES: Associate[] = [
  {
    name: 'Constance',
    persona: 'Compliance Analyst',
    description: 'Runs SOC 2 gap assessments, maps controls to TSC criteria, and produces signed compliance outcomes that feed into your Compliance Rank.',
    track: 'CR',
    trackLabel: 'Compliance Rank',
    trackColor: 'blue',
    icon: <Shield className="w-8 h-8" />,
    packageName: '@trustcenter/skill-soc2-gap-assessment',
    cliCommand: 'trustcenter-soc2',
    skills: [
      {
        id: 'SOC2-GAP',
        name: 'SOC 2 Gap Assessment',
        description: 'Maps your controls to CC1–CC9, A1, C1 criteria. Produces per-category gap findings with remediation guidance.',
        frameworks: ['SOC 2 Type 2', 'SOC 2 Type 1', 'ISO 27001:2022'],
      },
    ],
    installCmd: 'npm install -g @trustcenter/skill-soc2-gap-assessment',
    exampleCmd: 'trustcenter-soc2 --tenant acme.com --controls ./controls.json --submit --api-key tc_xxx',
  },
  {
    name: 'Soren',
    persona: 'Security Engineer',
    description: 'Scans SBOMs and package manifests for CVEs via the OSV API, producing signed vulnerability findings that feed into your Security Rank.',
    track: 'SR',
    trackLabel: 'Security Rank',
    trackColor: 'red',
    icon: <Lock className="w-8 h-8" />,
    packageName: '@trustcenter/skill-sbom-cve-scan',
    cliCommand: 'trustcenter-sbom',
    skills: [
      {
        id: 'SBOM-CVE',
        name: 'SBOM / CVE Scan',
        description: 'Queries OSV.dev for known vulnerabilities across your dependency tree. Critical and high severity CVEs are surfaced with CVSS scores.',
        frameworks: ['NIST CSF', 'CIS Controls v8'],
      },
    ],
    installCmd: 'npm install -g @trustcenter/skill-sbom-cve-scan',
    exampleCmd: 'trustcenter-sbom --tenant acme.com --manifest ./package.json --submit --api-key tc_xxx',
  },
  {
    name: 'Pria',
    persona: 'Privacy Counsel',
    description: 'Assesses GDPR and CCPA readiness from a privacy inventory questionnaire, producing signed privacy findings that feed into your Privacy Rank.',
    track: 'PR',
    trackLabel: 'Privacy Rank',
    trackColor: 'purple',
    icon: <Globe className="w-8 h-8" />,
    packageName: '@trustcenter/skill-gdpr-assessment',
    cliCommand: 'trustcenter-gdpr',
    skills: [
      {
        id: 'GDPR-CCPA',
        name: 'GDPR & CCPA Assessment',
        description: 'Evaluates 14 controls across lawful basis, DSRs, breach notification, encryption, retention, and cross-border transfers.',
        frameworks: ['GDPR', 'CCPA'],
      },
    ],
    installCmd: 'npm install -g @trustcenter/skill-gdpr-assessment',
    exampleCmd: 'trustcenter-gdpr --tenant acme.com --inventory ./privacy.json --submit --api-key tc_xxx',
  },
]

const TRACK_COLORS: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-800',
  },
  red: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-800',
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-800',
  },
}

export default function AssociatesTab() {
  const [copiedCmd, setCopiedCmd] = React.useState<string | null>(null)

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopiedCmd(key)
    setTimeout(() => setCopiedCmd(null), 1500)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 rounded-lg">
            <Package className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Skill Associates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Open-source agentic skills that assess your security, compliance, and privacy controls.
              Each skill produces a signed outcome that submits to the registry and increases your rank scores.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="font-semibold text-gray-900">Free to run</div>
            <div className="text-gray-500 text-xs mt-0.5">No cost to self-assess</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="font-semibold text-gray-900">Signed outcomes</div>
            <div className="text-gray-500 text-xs mt-0.5">SHA-256 tamper detection</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="font-semibold text-gray-900">Feeds rank</div>
            <div className="text-gray-500 text-xs mt-0.5">CR · SR · PR scores</div>
          </div>
        </div>
      </div>

      {/* Associate Cards */}
      <div className="grid grid-cols-1 gap-6">
        {ASSOCIATES.map((associate) => {
          const colors = TRACK_COLORS[associate.trackColor]
          return (
            <div key={associate.name} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Card Header */}
              <div className={`${colors.bg} ${colors.border} border-b px-6 py-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`${colors.text}`}>{associate.icon}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`text-lg font-bold ${colors.text}`}>{associate.name}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
                          {associate.persona}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{associate.description}</p>
                    </div>
                  </div>
                  <div className={`text-right shrink-0 ml-4`}>
                    <div className={`text-2xl font-bold ${colors.text}`}>{associate.track}</div>
                    <div className="text-xs text-gray-500">{associate.trackLabel}</div>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="px-6 py-4 border-b border-gray-100">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Skills</h4>
                {associate.skills.map((skill) => (
                  <div key={skill.id} className="flex items-start gap-3">
                    <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded mt-0.5 shrink-0">
                      {skill.id}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{skill.name}</div>
                      <div className="text-sm text-gray-500">{skill.description}</div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {skill.frameworks.map((fw) => (
                          <span key={fw} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {fw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Install */}
              <div className="px-6 py-4 space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Get Started</h4>

                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-gray-900 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Terminal className="w-4 h-4 text-gray-400 shrink-0" />
                      <code className="text-sm text-green-400 truncate">{associate.installCmd}</code>
                    </div>
                    <button
                      onClick={() => copy(associate.installCmd, `${associate.name}-install`)}
                      className="ml-3 text-xs text-gray-400 hover:text-white shrink-0 transition-colors"
                    >
                      {copiedCmd === `${associate.name}-install` ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between bg-gray-900 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Terminal className="w-4 h-4 text-gray-400 shrink-0" />
                      <code className="text-sm text-blue-300 truncate">{associate.exampleCmd}</code>
                    </div>
                    <button
                      onClick={() => copy(associate.exampleCmd, `${associate.name}-example`)}
                      className="ml-3 text-xs text-gray-400 hover:text-white shrink-0 transition-colors"
                    >
                      {copiedCmd === `${associate.name}-example` ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 font-mono text-xs">{associate.packageName}</span>
                  <a
                    href={`https://www.npmjs.com/package/${associate.packageName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 text-xs font-medium"
                  >
                    npm <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <strong>Self-assessed outcomes (1.0× multiplier)</strong> are accepted immediately. Submitting outcomes verified by
        a registry-trusted auditor or after red-team testing unlocks higher multipliers (up to 2.0×) and faster rank growth.
        See the <span className="font-mono">Skills &amp; Rank</span> tab for your current multiplier.
      </div>
    </div>
  )
}
